import { useEffect, useState } from "react";

/**
 * How often a surface re-measures its offset from the server's clock.
 *
 * Fifteen seconds, and the number is not doing much work: a device's clock does not drift
 * perceptibly in a quarter of a minute, so between samples each surface counts on its own
 * and the display is smooth. What the resync catches is the step changes — a laptop waking
 * from sleep, an NTP correction landing, a phone crossing a network that fixes its time.
 */
const SYNC_MS = 15_000;

/**
 * A tolerance, so a resync that learns nothing does not re-render everything watching.
 *
 * Round-trip halving is an estimate, so consecutive samples wobble by a few tens of
 * milliseconds even against a perfectly steady clock. Writing that wobble into state would
 * re-render every session bar on the page four times a minute to move a counter by nothing.
 */
const NOISE_MS = 250;

/**
 * This browser's offset from the SERVER's clock, in ms: add it to `Date.now()` to get the
 * server's instant.
 *
 * A goon session's `startedAt` is a server instant, and every surface that shows the timer
 * was subtracting it from its own `Date.now()` — which does not measure the session, it
 * measures how well that particular machine agrees with the server. Two devices watching
 * one session disagreed by exactly their clock difference, which on an unsynced desktop is
 * routinely a minute. The session is one thing; its clock has to be one clock.
 *
 * SAMPLED, NOT STREAMED. `/api/ping` already exists and answers with `now`, so the
 * measurement is a fetch every SYNC_MS rather than anything on the socket, and between
 * samples the caller's own one-second tick does the counting. That is the whole design:
 * agree on the offset occasionally, count locally always.
 *
 * The offset is corrected for latency by half the round trip — the usual estimate, and the
 * right one here, where being 20ms out on a counter that renders whole seconds is free.
 *
 * Starts at 0, which is the honest answer before the first sample: no information about
 * the server's clock is the same as assuming this one is right, and it is what the app did
 * unconditionally before. The first sample lands within a frame or two of mount.
 */
export function useServerClockOffset(syncMs = SYNC_MS, enabled = true): number {
  const [offset, setOffset] = useState(0);

  useEffect(() => {
    // Sampling only while something reads the answer (2026-09-10). The Faps cap nudge
    // mounts on every screen and page and sampled every 15 s for a day-stamp it reads only
    // after a capped tap — a request every screen made for a clock it was not showing.
    // Disabled, the last offset stands; the first sample after enabling lands within a
    // frame, before anything is rendered from it.
    if (!enabled) return;
    let live = true;
    let timer: ReturnType<typeof setTimeout>;

    const sample = async () => {
      try {
        const sent = Date.now();
        const r = await fetch("/api/ping", { cache: "no-store" });
        const { now } = (await r.json()) as { now?: number };
        const received = Date.now();
        if (live && typeof now === "number") {
          // Half the round trip: the server stamped `now` somewhere in the middle of it.
          const next = now + (received - sent) / 2 - received;
          setOffset((cur) => (Math.abs(next - cur) > NOISE_MS ? next : cur));
        }
      } catch {
        // Keep the offset we have. A missed sample is not a reason to jump the clock back
        // to "trust this machine", and the socket's own reconnect reports the outage.
      }
      if (live) timer = setTimeout(() => void sample(), syncMs);
    };

    void sample();
    return () => {
      live = false;
      clearTimeout(timer);
    };
  }, [syncMs, enabled]);

  return offset;
}
